import { useEffect, useState } from 'react';
import { submitMessage } from '@/api/message';
import { getProductTypeDict, getMessageTypeDict, type DictData } from '@/api/dict';

const emptyForm = { userName: '', companyName: '', contactInfo: '', productType: '', messageType: '', messageContent: '' };

interface ConsultationFormProps {
  /** 顶部小标签 */
  badge?: string;
  /** 主标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
}

function ConsultationForm({
  badge = '在线咨询',
  title = '在线咨询 / 申领样品',
  subtitle = '填写以下信息，我们的商务团队将在 24 小时内与您联系',
}: ConsultationFormProps) {
  const [formSent, setFormSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [productOptions, setProductOptions] = useState<DictData[]>([]);
  const [messageOptions, setMessageOptions] = useState<DictData[]>([]);

  useEffect(() => {
    getProductTypeDict().then((res) => setProductOptions(res.data ?? [])).catch(() => {});
    getMessageTypeDict().then((res) => setMessageOptions(res.data ?? [])).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitMessage(formData);
      setFormSent(true);
      setFormData(emptyForm);
    } catch (err) {
      alert(err instanceof Error ? err.message : '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">{badge}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">{title}</h2>
          <p className="text-sm text-foreground-500">{subtitle}</p>
        </div>
        {formSent ? (
          <div className="bg-accent-50 border border-accent-200 rounded-2xl p-8 md:p-10 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-5"><i className="ri-check-line text-2xl"></i></div>
            <h3 className="text-xl font-bold text-foreground-900 mb-2">提交成功！</h3>
            <p className="text-sm text-foreground-500">感谢您的咨询，我们将尽快与您联系。</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className="block text-xs font-semibold text-foreground-600 mb-2">姓名 <span className="text-accent-500">*</span></label><input type="text" name="userName" required value={formData.userName} onChange={(e) => setFormData({ ...formData, userName: e.target.value })} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50" placeholder="请输入姓名" /></div>
              <div><label className="block text-xs font-semibold text-foreground-600 mb-2">单位名称</label><input type="text" name="companyName" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50" placeholder="请输入单位名称" /></div>
            </div>
            <div><label className="block text-xs font-semibold text-foreground-600 mb-2">联系方式 <span className="text-accent-500">*</span></label><input type="text" name="contactInfo" required value={formData.contactInfo} onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50" placeholder="手机号 / 邮箱" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className="block text-xs font-semibold text-foreground-600 mb-2">需求产品</label><select name="productType" value={formData.productType} onChange={(e) => setFormData({ ...formData, productType: e.target.value })} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50"><option value="">请选择</option>{productOptions.map((o) => (<option key={o.dictValue} value={o.dictValue}>{o.dictLabel}</option>))}</select></div>
              <div><label className="block text-xs font-semibold text-foreground-600 mb-2">咨询类型</label><select name="messageType" value={formData.messageType} onChange={(e) => setFormData({ ...formData, messageType: e.target.value })} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50"><option value="">请选择</option>{messageOptions.map((o) => (<option key={o.dictValue} value={o.dictValue}>{o.dictLabel}</option>))}</select></div>
            </div>
            <div><label className="block text-xs font-semibold text-foreground-600 mb-2">留言内容</label><textarea name="messageContent" maxLength={500} value={formData.messageContent} onChange={(e) => setFormData({ ...formData, messageContent: e.target.value })} rows={3} className="w-full px-4 py-3 text-sm border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all bg-background-50 resize-none" placeholder="请描述您的具体需求（选填）" /><p className="text-xs text-foreground-400 mt-1 text-right">{formData.messageContent.length}/500</p></div>
            <button type="submit" disabled={submitting} className="w-full py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">{submitting ? '提交中...' : <>提交咨询 <i className="ri-arrow-right-line ml-1.5"></i></>}</button>
          </form>
        )}
      </div>
    </section>
  );
}

export default ConsultationForm;
